/**
 * useFeature hook — single source of truth for frontend feature flag visibility.
 *
 * Wraps GET /api/feature-flags (role-aware) and exposes:
 *   const { isVisible, status, loading } = useFeature("conveyancing_certificates");
 *
 * For multi-flag gating use:
 *   const { allVisible } = useFeatures(["property_sharing", "property_transfers"]);
 *
 * The hook caches the flag map on `window.__SCWT_FLAGS__` so it's fetched once
 * per page navigation, refreshed on auth changes (mount triggers a fetch when
 * cache is empty).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;
let _inflight = null;
const _listeners = new Set();

async function _load(force = false) {
  if (!force && _cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/feature-flags")
    .then(({ data }) => { _cache = data || {}; return _cache; })
    .catch(() => { _cache = {}; return _cache; })
    .finally(() => { _inflight = null; _listeners.forEach((fn) => fn(_cache)); });
  return _inflight;
}

/** Force-refresh after login / logout. */
export function refreshFeatureFlags() { return _load(true); }

/** Returns visibility for a single flag key. */
export function useFeature(key) {
  const [snap, setSnap] = useState(_cache);
  useEffect(() => {
    const fn = (m) => setSnap({ ...m });
    _listeners.add(fn);
    if (!_cache) _load();
    else setSnap(_cache);
    return () => _listeners.delete(fn);
  }, []);
  const entry = (snap && snap[key]) || null;
  return {
    loading: !snap,
    status: entry?.status || null,
    isVisible: !!entry?.visible,
  };
}

/** Returns combined visibility for several flag keys. */
export function useFeatures(keys) {
  const [snap, setSnap] = useState(_cache);
  useEffect(() => {
    const fn = (m) => setSnap({ ...m });
    _listeners.add(fn);
    if (!_cache) _load();
    else setSnap(_cache);
    return () => _listeners.delete(fn);
  }, []);
  if (!snap) return { loading: true, flags: {}, allVisible: false, anyVisible: false };
  const flags = {};
  let allVisible = true, anyVisible = false;
  for (const k of keys) {
    const e = snap[k];
    flags[k] = !!e?.visible;
    if (!e?.visible) allVisible = false;
    if (e?.visible) anyVisible = true;
  }
  return { loading: false, flags, allVisible, anyVisible };
}


/** Conditional-render wrapper: <FeatureGate flag="property_sharing">{...}</FeatureGate> */
export function FeatureGate({ flag, fallback = null, children }) {
  const { isVisible, loading } = useFeature(flag);
  if (loading) return null;
  return isVisible ? children : fallback;
}
